/**
 * Annotation Workbench Page
 * Main interface for annotators to label tasks
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Inbox, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  AnnotationForm,
  useNextTask,
  useSkipTask,
  useSubmitLabel,
  useBatch,
} from '@/features/annotations';
import type { AnnotationTask, LabelCreate } from '@/types/annotation';

export function AnnotationWorkbenchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batch') || undefined;

  const [currentTask, setCurrentTask] = useState<AnnotationTask | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [startTime, setStartTime] = useState<number>(Date.now());

  const { data: batch } = useBatch(batchId || '');
  const nextTaskMutation = useNextTask(batchId);
  const skipMutation = useSkipTask();
  const submitMutation = useSubmitLabel();

  // Fetch next task
  const fetchNextTask = useCallback(async () => {
    try {
      const task = await nextTaskMutation.mutateAsync();
      setCurrentTask(task);
      setStartTime(Date.now());
    } catch (error) {
      console.error('Failed to fetch next task:', error);
      setCurrentTask(null);
    }
  }, [nextTaskMutation]);

  // Initial load
  useEffect(() => {
    fetchNextTask();
  }, []);

  const handleSkip = async () => {
    if (!currentTask) return;

    try {
      await skipMutation.mutateAsync(currentTask.id);
      fetchNextTask();
    } catch (error) {
      console.error('Failed to skip task:', error);
    }
  };

  const handleSubmit = async (data: LabelCreate) => {
    if (!currentTask) return;

    try {
      await submitMutation.mutateAsync({
        taskId: currentTask.id,
        data,
      });
      setCompletedCount((prev) => prev + 1);
      fetchNextTask();
    } catch (error) {
      console.error('Failed to submit label:', error);
      alert('提交失败: ' + (error as Error).message);
    }
  };

  const isLoading = nextTaskMutation.isPending;
  const isSubmitting = submitMutation.isPending || skipMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/annotation/batches')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">标注工作台</h1>
              {batch && (
                <p className="text-sm text-gray-500">{batch.title}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500" />
              本次已完成: <span className="font-medium text-gray-900">{completedCount}</span>
            </div>
            {batch && (
              <div className="text-sm text-gray-600">
                批次进度: <span className="font-medium text-gray-900">
                  {batch.completed_tasks}/{batch.total_tasks}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-gray-500">加载任务中...</p>
            </div>
          </div>
        )}

        {/* No Task Available */}
        {!isLoading && !currentTask && (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {completedCount > 0 ? '太棒了！任务完成' : '暂无待标注任务'}
            </h2>
            <p className="text-gray-600 mb-6">
              {completedCount > 0
                ? `本次共完成 ${completedCount} 条标注`
                : '当前批次中没有可标注的任务'}
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button variant="secondary" onClick={() => navigate('/annotation/batches')}>
                返回批次列表
              </Button>
              <Button variant="primary" onClick={() => navigate('/annotation/stats')}>
                查看统计
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Task Card */}
        {!isLoading && currentTask && (
          <div className="space-y-6">
            {/* Task Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-500 font-mono">
                  {currentTask.task_id}
                </span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                  待标注
                </span>
              </div>

              {/* Original Text */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-lg text-gray-900 leading-relaxed">
                  {currentTask.original_text}
                </p>
              </div>

              <div className="text-sm text-gray-500 flex items-center gap-4">
                <span>字数: {currentTask.original_text.length}</span>
                {currentTask.source_session_id && (
                  <span>会话: {currentTask.source_session_id.slice(0, 8)}...</span>
                )}
              </div>
            </div>

            {/* Annotation Form */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">标注内容</h3>
              <AnnotationForm
                onSubmit={handleSubmit}
                onSkip={handleSkip}
                isSubmitting={isSubmitting}
                startTime={startTime}
              />
            </div>
          </div>
        )}
      </div>

      {/* Keyboard Shortcuts Help */}
      <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-xs text-gray-500">
        <div className="font-medium text-gray-700 mb-1">快捷键</div>
        <div className="space-y-0.5">
          <div><kbd className="px-1 bg-gray-100 rounded">1</kbd> 正面</div>
          <div><kbd className="px-1 bg-gray-100 rounded">2</kbd> 中性</div>
          <div><kbd className="px-1 bg-gray-100 rounded">3</kbd> 负面</div>
          <div><kbd className="px-1 bg-gray-100 rounded">⌘S/Ctrl+S</kbd> 提交</div>
          <div><kbd className="px-1 bg-gray-100 rounded">Esc</kbd> 跳过</div>
        </div>
      </div>
    </div>
  );
}
